<script>
import { mapGetters } from 'vuex';
import { marked } from 'marked';

marked.setOptions({
    breaks: true,
    gfm: true,
});

export default {
    data() {
        return {
            currentPage: 1,
            selectedUpdate: null,
        };
    },
    computed: {
        ...mapGetters({
            records: 'platformUpdates/getRecords',
            meta: 'platformUpdates/getMeta',
            uiFlags: 'platformUpdates/getUIFlags',
        }),
        hasPrevPage() {
            return this.currentPage > 1;
        },
        hasNextPage() {
            return this.meta.total_pages && this.currentPage < this.meta.total_pages;
        },
    },
    mounted() {
        this.fetchUpdates();
        this.$store.dispatch('platformUpdates/markAllRead');
        document.addEventListener('keydown', this.onKeyDown);
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.onKeyDown);
    },
    methods: {
        fetchUpdates() {
            this.$store.dispatch('platformUpdates/get', this.currentPage);
        },
        onPageChange(page) {
            this.currentPage = page;
            this.fetchUpdates();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },
        openModal(update) {
            this.selectedUpdate = update;
            document.body.style.overflow = 'hidden';
        },
        closeModal() {
            this.selectedUpdate = null;
            document.body.style.overflow = '';
        },
        onKeyDown(e) {
            if (e.key === 'Escape' && this.selectedUpdate) {
                this.closeModal();
            }
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            return d.toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
            });
        },
        relativeDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            const now = new Date();
            const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
            if (diff === 0) return 'Hoje';
            if (diff === 1) return 'Ontem';
            if (diff < 7) return `${diff} dias atrás`;
            return this.formatDate(dateStr);
        },
        isImage(contentType) {
            return contentType && contentType.startsWith('image/');
        },
        isVideo(contentType) {
            return contentType && contentType.startsWith('video/');
        },
        renderMarkdown(text) {
            if (!text) return '';
            return marked.parse(text);
        },
        truncate(text, maxLen = 120) {
            if (!text) return '';
            return text.length > maxLen ? text.slice(0, maxLen) + '…' : text;
        },
    },
};
</script>

<template>
    <div class="flex-1 overflow-auto">
        <div class="max-w-3xl mx-auto px-6 py-8">
            <div class="mb-8">
                <h1 class="text-2xl font-bold text-n-slate-12">
                    🚀 Novidades
                </h1>
                <p class="text-n-slate-10 text-sm mt-1">
                    Acompanhe as últimas atualizações e melhorias.
                </p>
            </div>

            <div v-if="uiFlags.isFetching" class="flex flex-col items-center justify-center py-20 gap-3">
                <div class="size-10 rounded-full border-2 border-n-slate-4 border-t-woot-500 animate-spin" />
                <span class="text-sm text-n-slate-10">Carregando novidades...</span>
            </div>

            <div v-else-if="!records.length" class="flex flex-col items-center justify-center py-20 text-center">
                <div class="flex items-center justify-center size-16 rounded-2xl bg-n-alpha-2 mb-5">
                    <span class="i-lucide-newspaper size-8 text-n-slate-9" />
                </div>
                <h3 class="text-lg font-semibold text-n-slate-12 mb-1">
                    Nenhuma novidade ainda
                </h3>
                <p class="text-n-slate-10 text-sm max-w-xs">
                    Em breve você verá aqui as últimas atualizações e melhorias da plataforma.
                </p>
            </div>

            <div v-else class="flex flex-col gap-3">
                <button v-for="update in records" :key="update.id"
                    class="w-full text-left bg-n-solid-2 rounded-xl border border-n-weak px-5 py-4 transition-all duration-150 hover:shadow-md hover:border-n-strong/50 hover:bg-n-solid-3 cursor-pointer group"
                    @click="openModal(update)">
                    <div class="flex items-start gap-4">
                        <div class="flex-shrink-0 mt-2">
                            <div class="size-2.5 rounded-full transition-colors" :class="update.is_read
                                ? 'bg-n-slate-5'
                                : 'bg-woot-500 ring-3 ring-woot-100 dark:ring-woot-900/30'" />
                        </div>

                        <div class="flex-1 min-w-0">
                            <div class="flex items-center gap-2 mb-0.5">
                                <h3 class="text-base font-semibold text-n-slate-12 truncate">
                                    {{ update.title }}
                                </h3>
                                <span v-if="!update.is_read"
                                    class="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-woot-500 text-white">
                                    Novo
                                </span>
                            </div>
                            <p v-if="update.subtitle" class="text-sm text-n-slate-10 line-clamp-2 leading-relaxed">
                                {{ update.subtitle }}
                            </p>
                        </div>

                        <div class="flex-shrink-0 flex items-center gap-3 text-n-slate-9">
                            <span class="text-xs whitespace-nowrap hidden sm:block">
                                {{ relativeDate(update.published_at) }}
                            </span>
                            <span
                                class="i-lucide-chevron-right size-4 transition-transform group-hover:translate-x-0.5" />
                        </div>
                    </div>
                </button>
            </div>

            <div v-if="meta.total_pages > 1" class="flex items-center justify-center mt-8 gap-2">
                <button class="flex items-center justify-center size-9 rounded-lg text-sm transition-all duration-150"
                    :class="hasPrevPage
                        ? 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 cursor-pointer'
                        : 'text-n-slate-6 cursor-not-allowed'" :disabled="!hasPrevPage"
                    @click="hasPrevPage && onPageChange(currentPage - 1)">
                    <span class="i-lucide-chevron-left size-4" />
                </button>

                <template v-for="page in meta.total_pages" :key="page">
                    <button
                        class="flex items-center justify-center size-9 rounded-lg text-sm font-medium transition-all duration-150"
                        :class="page === currentPage
                            ? 'bg-woot-500 text-white shadow-sm'
                            : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 hover:text-n-slate-12'"
                        @click="onPageChange(page)">
                        {{ page }}
                    </button>
                </template>

                <button class="flex items-center justify-center size-9 rounded-lg text-sm transition-all duration-150"
                    :class="hasNextPage
                        ? 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 cursor-pointer'
                        : 'text-n-slate-6 cursor-not-allowed'" :disabled="!hasNextPage"
                    @click="hasNextPage && onPageChange(currentPage + 1)">
                    <span class="i-lucide-chevron-right size-4" />
                </button>
            </div>


            <div v-if="meta.total_count" class="text-center mt-3 text-xs text-n-slate-9">
                {{ meta.total_count }} {{ meta.total_count === 1 ? 'atualização' : 'atualizações' }} no total
            </div>

        </div>

        <Teleport to="body">
            <Transition name="modal-fade">
                <div v-if="selectedUpdate" class="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6"
                    @click.self="closeModal">
                    <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="closeModal" />

                    <div
                        class="relative w-full max-w-2xl max-h-[85vh] bg-n-solid-2 rounded-2xl border border-n-weak shadow-2xl flex flex-col overflow-hidden z-10">
                        <div
                            class="flex items-center justify-between px-6 py-4 border-b border-n-weak bg-n-alpha-1 flex-shrink-0">
                            <div class="flex items-center gap-2.5">
                                <span class="i-lucide-calendar size-4 text-n-slate-10" />
                                <time class="text-xs font-medium text-n-slate-10 uppercase tracking-wider">
                                    {{ formatDate(selectedUpdate.published_at) }}
                                </time>
                                <span class="text-n-slate-6">·</span>
                                <span class="text-xs text-n-slate-9">
                                    {{ relativeDate(selectedUpdate.published_at) }}
                                </span>
                                <span v-if="!selectedUpdate.is_read"
                                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-woot-500 text-white">
                                    <span class="i-lucide-sparkles size-3" />
                                    Novo
                                </span>
                            </div>
                            <button
                                class="flex items-center justify-center size-8 rounded-lg bg-n-alpha-2 hover:bg-n-alpha-3 transition-colors text-n-slate-10 hover:text-n-slate-12"
                                @click="closeModal">
                                <span class="i-lucide-x size-4" />
                            </button>
                        </div>

                        <div class="flex-1 overflow-y-auto px-6 py-6">
                            <h2 class="text-xl font-bold text-n-slate-12 mb-1 leading-tight">
                                {{ selectedUpdate.title }}
                            </h2>
                            <p v-if="selectedUpdate.subtitle" class="text-sm text-n-slate-10 mb-5 leading-relaxed">
                                {{ selectedUpdate.subtitle }}
                            </p>

                            <div v-if="selectedUpdate.description" class="markdown-body text-n-slate-12"
                                v-html="renderMarkdown(selectedUpdate.description)" />

                            <div v-if="selectedUpdate.media && selectedUpdate.media.length" class="mt-6 grid gap-3"
                                :class="selectedUpdate.media.length > 1 ? 'grid-cols-2' : 'grid-cols-1'">
                                <template v-for="m in selectedUpdate.media" :key="m.id">
                                    <a v-if="isImage(m.content_type)" :href="m.url" target="_blank"
                                        class="block overflow-hidden rounded-xl border border-n-weak group/img">
                                        <img :src="m.url" :alt="m.filename"
                                            class="w-full h-auto max-h-80 object-cover transition-transform duration-300 group-hover/img:scale-[1.02]" />
                                    </a>
                                    <video v-else-if="isVideo(m.content_type)" :src="m.url" controls
                                        class="w-full rounded-xl border border-n-weak max-h-80" />
                                    <a v-else :href="m.url" target="_blank"
                                        class="flex items-center gap-3 px-4 py-3 rounded-xl border border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors">
                                        <span class="i-lucide-file-text size-5 text-n-slate-10" />
                                        <span class="text-sm text-n-slate-11 truncate">{{ m.filename }}</span>
                                        <span class="i-lucide-external-link size-3.5 text-n-slate-9 ml-auto" />
                                    </a>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
        </Teleport>
    </div>
</template>

<style scoped>
/* Modal transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.2s ease;
}

.modal-fade-enter-active .relative,
.modal-fade-leave-active .relative {
    transition: transform 0.2s ease, opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
}

.modal-fade-enter-from .relative {
    transform: scale(0.95) translateY(10px);
    opacity: 0;
}

.modal-fade-leave-to .relative {
    transform: scale(0.95) translateY(10px);
    opacity: 0;
}

/* Markdown styles */
.markdown-body :deep(h1) {
    font-size: 1.5rem;
    font-weight: 700;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
    line-height: 1.3;
}

.markdown-body :deep(h2) {
    font-size: 1.25rem;
    font-weight: 700;
    margin-top: 1.25rem;
    margin-bottom: 0.5rem;
    line-height: 1.35;
}

.markdown-body :deep(h3) {
    font-size: 1.1rem;
    font-weight: 600;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
}

.markdown-body :deep(p) {
    margin-bottom: 0.75rem;
    line-height: 1.7;
    font-size: 0.9375rem;
}

.markdown-body :deep(ul),
.markdown-body :deep(ol) {
    padding-left: 1.5rem;
    margin-bottom: 0.75rem;
}

.markdown-body :deep(ul) {
    list-style-type: disc;
}

.markdown-body :deep(ol) {
    list-style-type: decimal;
}

.markdown-body :deep(li) {
    margin-bottom: 0.35rem;
    line-height: 1.6;
    font-size: 0.9375rem;
}

.markdown-body :deep(blockquote) {
    border-left: 3px solid var(--color-woot-500, #1f93ff);
    padding: 0.5rem 1rem;
    margin: 1rem 0;
    border-radius: 0 0.5rem 0.5rem 0;
    font-style: italic;
}

.markdown-body :deep(code) {
    padding: 0.15rem 0.4rem;
    border-radius: 0.375rem;
    font-size: 0.85rem;
    font-family: ui-monospace, monospace;
}

.markdown-body :deep(pre) {
    padding: 1rem;
    border-radius: 0.75rem;
    overflow-x: auto;
    margin: 1rem 0;
    font-size: 0.85rem;
}

.markdown-body :deep(pre code) {
    padding: 0;
    background: none;
}

.markdown-body :deep(a) {
    color: var(--color-woot-500, #1f93ff);
    text-decoration: underline;
    text-underline-offset: 2px;
}

.markdown-body :deep(a:hover) {
    opacity: 0.8;
}

.markdown-body :deep(strong) {
    font-weight: 600;
}

.markdown-body :deep(hr) {
    margin: 1.5rem 0;
    border: none;
    height: 1px;
}

.markdown-body :deep(img) {
    border-radius: 0.75rem;
    max-width: 100%;
    height: auto;
    margin: 1rem 0;
}

.markdown-body :deep(table) {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0;
    font-size: 0.9rem;
}

.markdown-body :deep(th),
.markdown-body :deep(td) {
    padding: 0.5rem 0.75rem;
    text-align: left;
}

.markdown-body :deep(th) {
    font-weight: 600;
}
</style>
